import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  Ticket,
  Receipt,
  TrendingUp,
  AlertTriangle,
  UserPlus,
  UserCheck,
  Server,
  Globe,
  Activity,
  Wrench,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  UniversalPage,
  Button,
  Card,
  NotificationCard,
  UniversalLoadingState,
  UniversalAsyncBoundary,
  StatCard,
  ChartMeter,
  ChartLine,
  ChartStack,
  ChartBars,
} from '@sighthost/ui';
import {
  adminDashboardService,
  REVENUE_MIX_LABELS,
  PAYMENT_GATEWAY_LABELS,
  type DashboardSnapshot,
  type CollectionsMonth,
  type RevenueMixRow,
  type PaymentMixRow,
} from '@/domains/admin/services/adminDashboardService';
import {
  adminActionQueueService,
  type ActionQueueRow,
  type ActionQueuePriority,
} from '@/domains/admin/services/adminActionQueueService';
import {
  adminCronService,
  type CronHealthRow,
  type CronLivenessRow,
} from '@/domains/admin/services/adminCronService';
import { todayScopedKey } from '@/shared/lib/queryClient';
// formatZARWhole, not a local Intl.NumberFormat. This page carried its own
// `formatZAR` and SalesDashboard its own `zar` — two more of the ~27 copies the
// shared formatter replaced on 2026-08-03. Output is byte-identical for every
// value these tiles show; the shared one additionally normalises the narrow
// no-break space Intl emits, which neither local copy did.
import { relativeTime, formatZARWhole } from '@/shared/utils/format';
import { DeveloperTagCard } from '@/shared/components/staff/DeveloperTagCard';

/**
 * AdminDashboard — operational home page at /admin.
 *
 * Spec: _bmad-output/ADMIN_PORTAL_GAP_ANALYSIS_2026-05-13.md §6 A-1.
 * Arch locks: Q2 (read-through view, no cache), Q6 (audit-log merging is
 *             C1 territory, not here).
 *
 * Purpose: answer "what needs my attention NOW?" Distinct from /admin/reports
 * which answers "how is the business trending?" — different lens, different
 * view (v_admin_dashboard_snapshot vs v_admin_dashboard_metrics).
 *
 * Refresh model: 60-second client-side setInterval. Stale data is acceptable;
 * we don't want realtime subscriptions for an operational glance.
 *
 * Composition (top → bottom):
 *   1. Headline KPIs (4 cards) — MRR · Active customers · Open tickets · Overdue invoices
 *   2. Needs-attention panel — counts of unassigned tickets, overdue invoices,
 *      expiring domains, stuck provisioning (stub for action queue B-1)
 *   3. Reseller capacity ribbon — gauge + link to /admin/hosting/server-health
 *   4. Cron health strip — 6 named runners, status dots (stubs until A-3 lands)
 *   5. Quick-action footer — Add customer + Import existing (kept from old layout)
 */

const REFRESH_INTERVAL_MS = 60_000;

/** Percent at which reseller capacity escalates into the attention panel.
 *  Matches the threshold the ribbon's own tone ladder switches on. */
const CAPACITY_ATTENTION_PCT = 80;
const CAPACITY_BREACH_PCT = 95;

/**
 * What NeedsAttentionPanel renders — deliberately NARROWER than ActionQueueRow.
 *
 * The panel is the page's escalation channel, but until now only
 * v_admin_action_queue could reach it. Reseller capacity could not, so at 88%
 * of the account cap it sat in a half-width card in row 3 with the same visual
 * weight as the cron pips.
 *
 * The synthetic capacity row joins the list through this type rather than by
 * widening ActionQueueSource, which mirrors the database view's enum — adding a
 * member there would claim the view can return a value it cannot.
 */
interface AttentionItem {
  key: string;
  priority: ActionQueuePriority;
  title: string;
  detail: string;
  href: string;
}

/** Merge the action queue with the synthetic capacity row. Capacity goes first:
 *  it is a whole-server condition, not one customer's problem. `null` in ->
 *  `null` out, so the panel can still tell "not loaded" from "nothing to do". */
function attentionItems(
  queue: ActionQueueRow[] | null,
  snapshot: DashboardSnapshot,
): AttentionItem[] | null {
  if (queue === null) return null;
  const capacity = capacityAttentionItem(snapshot);
  const fromQueue = queue.map((row) => ({
    key: `${row.source}:${row.entity_id}`,
    priority: row.priority,
    title: row.title,
    detail: row.detail,
    href: row.href,
  }));
  return capacity ? [capacity, ...fromQueue] : fromQueue;
}

/** The capacity escalation row, or null while there is headroom. */
function capacityAttentionItem(snapshot: DashboardSnapshot): AttentionItem | null {
  const pct = snapshot.reseller_capacity_pct;
  const max = snapshot.reseller_max_accounts;
  if (pct == null || max == null || pct < CAPACITY_ATTENTION_PCT) return null;
  const used = Math.round((pct / 100) * max);
  return {
    key: 'reseller_capacity:rs61',
    priority: pct >= CAPACITY_BREACH_PCT ? 'breach' : 'attention',
    title: `Reseller capacity at ${pct}%`,
    detail: `${used} of ${max} accounts used — ${Math.max(max - used, 0)} left before new provisioning fails.`,
    href: '/admin/hosting/server-health',
  };
}

export default function AdminDashboard() {
  // Phase 6. This was six useStates, a hand-rolled setInterval and a manual
  // per-panel .then/.catch fan-out. Each panel is now its own query, so they
  // still succeed and fail independently (the Phase 3.1 property — a snapshot
  // failure must not discard a healthy 82ms action queue) but with retry,
  // dedupe and stale-while-revalidate for free.
  //
  // The queries are scoped to the SAST day: at midnight the key changes and the
  // cache invalidates itself, instead of a tab left open overnight quietly
  // serving yesterday's figures (architect ruling, 2026-08-03).
  const snapshotQ = useQuery({
    queryKey: todayScopedKey('admin', 'dashboard', 'snapshot'),
    queryFn: () => adminDashboardService.getSnapshot(),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
  const queueQ = useQuery({
    queryKey: todayScopedKey('admin', 'dashboard', 'action-queue'),
    queryFn: () => adminActionQueueService.list(),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
  const trendQ = useQuery({
    queryKey: todayScopedKey('admin', 'dashboard', 'trend'),
    queryFn: () => adminDashboardService.getTrend(),
  });
  const cronsQ = useQuery({
    queryKey: todayScopedKey('admin', 'dashboard', 'cron-health'),
    queryFn: () => adminCronService.list(),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
  // THE SIGNAL THAT ACTUALLY PROVES A JOB RAN (F-01).
  //
  // `list()` reads cron.job_run_details — per CLAUDE.md §4.8 that records
  // 'succeeded' when the platform answered 401 or 500 and the function never
  // executed. This strip showed only that, which is why it would have rendered
  // a full row of green pips through the 16-day June outage, the 18-day July
  // one and the 2-day August one.
  //
  // `liveness()` reads heartbeats + audit events — the function's own proof it
  // ran. /admin/ops/cron-health has shown both since F4b; the dashboard, which
  // is the page people actually open, did not.
  //
  // Its own query, like every other panel: liveness failing must not discard a
  // healthy gateway read, and vice versa.
  const cronLivenessQ = useQuery({
    queryKey: todayScopedKey('admin', 'dashboard', 'cron-liveness'),
    queryFn: () => adminCronService.liveness(),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
  // Own queries, like every other panel — a chart failing must not discard a
  // healthy action queue. Neither refetches on the 60s interval: monthly
  // aggregates do not move minute to minute, and the SAST-scoped key already
  // invalidates them at midnight.
  const collectionsQ = useQuery({
    queryKey: todayScopedKey('admin', 'dashboard', 'collections'),
    queryFn: () => adminDashboardService.getCollectionsMonthly(),
  });
  const mixQ = useQuery({
    queryKey: todayScopedKey('admin', 'dashboard', 'revenue-mix'),
    queryFn: () => adminDashboardService.getRevenueMix(),
  });
  const payMixQ = useQuery({
    queryKey: todayScopedKey('admin', 'dashboard', 'payment-mix'),
    queryFn: () => adminDashboardService.getPaymentMix(),
  });

  const snapshot = snapshotQ.data ?? null;
  const queue = queueQ.data ?? null;
  const trend = trendQ.data ?? null;
  const crons = cronsQ.data ?? null;
  const cronLiveness = cronLivenessQ.data ?? null;

  const msg = (e: unknown) => (e instanceof Error ? e.message : e ? String(e) : null);
  const error = msg(snapshotQ.error);
  const queueError = msg(queueQ.error);
  const cronsError = msg(cronsQ.error);
  const cronLivenessError = msg(cronLivenessQ.error);
  // dataUpdatedAt is 0 until the first successful fetch.
  const refreshedAt = snapshotQ.dataUpdatedAt ? new Date(snapshotQ.dataUpdatedAt) : null;

  if (!snapshot && !error) {
    return (
      <UniversalPage title="Admin">
        <UniversalLoadingState variant="rows" count={4} />
      </UniversalPage>
    );
  }

  return (
    <UniversalPage
      title="Admin"
      description="Operational view. Refreshes every 60 seconds."
    >
      {error && (
        <NotificationCard
          tone="danger"
          title="Could not load dashboard"
          body={error}
        />
      )}

      {snapshot && (
        <div className="space-y-6">
          {/* Phase 7 — ORDER IS THE DESIGN.
              This page states its own purpose in the header comment: "what
              needs my attention NOW?". It used to open with four equal-weight
              KPI cards and bury the action queue beneath them, so the first
              thing the eye landed on was MRR — a number that changes monthly —
              rather than the ticket breaching SLA right now.
              Attention first, at full width. Metrics demoted to a compact
              strip. Operational health side-by-side below that. */}

          {/* 1. What needs doing — the loudest thing on the page.
                 Capacity escalates in here once it crosses its own threshold;
                 the ribbon below keeps the gauge and the detail. */}
          <NeedsAttentionPanel
            items={attentionItems(queue, snapshot)}
            queueTotal={queue?.length ?? 0}
            error={queueError}
          />

          {/* 2. Headline KPIs — context, not the headline. */}
          <section
            className="grid grid-cols-2 gap-3 lg:grid-cols-4"
            aria-label="Headline metrics"
          >
            <StatCard
              label="MRR"
              value={formatZARWhole(snapshot.mrr_zar)}
              icon={TrendingUp}
              tone="success"
              interactive={false}
              size="compact"
              sub={`Revenue MTD: ${formatZARWhole(snapshot.revenue_mtd_zar)}`}
              link={{ to: '/admin/reports', label: 'Open reports' }}
              delta={trend?.mrrDeltaPct != null ? `${trend.mrrDeltaPct > 0 ? '+' : ''}${trend.mrrDeltaPct}%` : undefined}
              trend={trend?.mrrDeltaPct != null ? (trend.mrrDeltaPct > 0 ? 'up' : trend.mrrDeltaPct < 0 ? 'down' : 'flat') : undefined}
            />
            <StatCard
              label="Active customers"
              value={snapshot.active_customers.toString()}
              icon={Users}
              tone="primary"
              interactive={false}
              size="compact"
              sub={
                snapshot.suspended_customers > 0
                  ? `${snapshot.suspended_customers} suspended`
                  : 'No suspensions'
              }
              link={{ to: '/admin/customers', label: 'Open customers' }}
              delta={trend?.customersDelta != null ? `${trend.customersDelta > 0 ? '+' : ''}${trend.customersDelta}` : undefined}
              trend={trend?.customersDelta != null ? (trend.customersDelta > 0 ? 'up' : trend.customersDelta < 0 ? 'down' : 'flat') : undefined}
            />
            <StatCard
              label="Open tickets"
              value={snapshot.open_tickets.toString()}
              icon={Ticket}
              tone={snapshot.unassigned_tickets > 0 ? 'warning' : 'info'}
              interactive={false}
              size="compact"
              sub={
                snapshot.unassigned_tickets > 0
                  ? `${snapshot.unassigned_tickets} unassigned`
                  : 'All assigned'
              }
              link={{ to: '/admin/support', label: 'Open inbox' }}
            />
            {/* "Overdue", not "Outstanding". The value now counts only invoices
                PAST their due date; an invoice issued today and payable next
                week is reported on the sub-line as "not yet due" instead of
                turning this card red. The tone was the actual defect — it went
                danger whenever anything was open, so a customer who had done
                nothing wrong made the dashboard look like a collections
                problem. */}
            <StatCard
              label="Overdue invoices"
              value={snapshot.overdue_invoices_count.toString()}
              icon={Receipt}
              tone={snapshot.overdue_invoices_count > 0 ? 'danger' : 'muted'}
              interactive={false}
              size="compact"
              sub={
                [
                  snapshot.invoices_in_grace > 0 ? `${snapshot.invoices_in_grace} in grace` : null,
                  snapshot.invoices_suspended > 0 ? `${snapshot.invoices_suspended} suspended` : null,
                  snapshot.due_invoices_count > 0 ? `${snapshot.due_invoices_count} not yet due` : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'All settled'
              }
              // `/admin/billing`, not `/admin/billing/invoices` — the latter has
              // no route, so the catch-all bounced the click back to /admin and
              // the tile appeared to do nothing.
              link={{ to: '/admin/billing', label: 'Open invoices' }}
            />
          </section>

          {/* 3. The money, over time and by source. Below the KPI strip
                 because these answer "how is it trending", which is a slower
                 question than "what needs doing" — and above operational
                 health because it is what the strip's MRR tile invites you to
                 ask next. */}
          <section className="grid grid-cols-1 gap-4 lg:grid-cols-2" aria-label="Revenue">
            <CollectionsCard query={collectionsQ} />
            <div className="flex flex-col gap-4">
              <RevenueMixCard query={mixQ} />
              <PaymentMixCard query={payMixQ} />
            </div>
          </section>

          {/* 4. Operational health — capacity and cron side by side; neither
                needs full width, and pairing them keeps the page shorter so
                the action queue stays above the fold. */}
          <section className="grid grid-cols-1 gap-4 lg:grid-cols-2" aria-label="Operational health">
            <ResellerCapacityRibbon snapshot={snapshot} />
            {/* Independent of the snapshot since A1. */}
            <CronHealthStrip
              crons={crons}
              liveness={cronLiveness}
              error={cronsError}
              livenessError={cronLivenessError}
            />
          </section>

          {/* Delivery tools — artefacts staff hand out or paste, not data.
              Above Quick actions rather than inside it: QuickActionCard is a
              4-up grid of single-link tiles, and this card carries two buttons
              and a caveat, so it would be the one wrong-shaped tile in a row
              of four. Also on /sales beside the bank letter — the same
              component, because a developer who is plain `staff` cannot reach
              /sales at all. */}
          <section aria-label="Delivery tools">
            <DeveloperTagCard />
          </section>

          {/* 5. Quick actions */}
          <section
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
            aria-label="Quick actions"
          >
            <QuickActionCard
              to="/sales/customers/new"
              icon={UserPlus}
              title="Add new customer"
              description="Sell hosting, Studio or a service. Bills first; hosting and the domain follow payment."
            />
            <QuickActionCard
              to="/admin/customers/import-existing"
              icon={UserCheck}
              title="Import existing cPanel customer"
              description="Onboard one of the pre-existing cPanel accounts to the portal."
            />
            <QuickActionCard
              to="/admin/ops/provisioning"
              icon={Wrench}
              title="Provisioning queue"
              description={
                snapshot.failed_provisioning_count + snapshot.pending_provisioning_count > 0
                  ? `${snapshot.pending_provisioning_count} pending, ${snapshot.failed_provisioning_count} failed`
                  : 'No active jobs.'
              }
            />
            <QuickActionCard
              to="/admin/domains/reconcile"
              icon={Globe}
              title="Domain reconciliation"
              description="Find domains in the reseller account that aren't yet linked to a portal customer."
            />
          </section>

          {refreshedAt && (
            <p className="text-xs text-muted-foreground">
              Refreshed {relativeTime(refreshedAt.toISOString())} · next refresh in ≤60s.
            </p>
          )}
        </div>
      )}
    </UniversalPage>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────

function NeedsAttentionPanel({
  items,
  queueTotal,
  error,
}: {
  items: AttentionItem[] | null;
  /** Action-queue rows only, EXCLUDING synthetic items. "See all N" links to
   *  /admin/ops/action-queue, which does not know about the capacity row —
   *  counting it here would send the reader to a page one row short. */
  queueTotal: number;
  error: string | null;
}) {
  // Phase 8: 3 collapsed, the rest behind an explicit "N more" toggle.
  // Phase 7 showed 8, sized for a panel that then carried 3-5 rows. The
  // 2026-08-30 action-queue fix took the invoice source from 3 rows to 15
  // (R287 -> R1,905 of overdue debt that had been filtered out), so 8 stopped
  // being "the top of the list" and became "most of a long list" — the panel
  // read as clutter and the reader stopped scanning it, which is the failure
  // this panel exists to prevent.
  //
  // Collapsed count is 3 rather than 5: the point is that the first screenful
  // is scannable in one glance. The counter carries the weight — a reader who
  // sees "12 more" knows the size of the problem WITHOUT the rows being
  // rendered, which "See all" alone never told them.
  const COLLAPSED_COUNT = 3;
  const [expanded, setExpanded] = useState(false);

  const all = items ?? [];
  // Breach count is over ALL items, never the visible slice. A hidden breach
  // is exactly the thing the badge exists to surface.
  const breachCount = all.filter((r) => r.priority === 'breach').length;
  const visible = expanded ? all : all.slice(0, COLLAPSED_COUNT);
  const hiddenCount = all.length - visible.length;

  return (
    <Card interactive={false} className="p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="size-5 text-warning" aria-hidden />
          <h2 className="font-display text-base font-bold tracking-tight text-foreground">
            Needs your attention
          </h2>
          {breachCount > 0 && (
            // bg-danger-dark, not bg-danger: white on #e53e3e is 4.13:1 and fails
            // AA at this size, and this is the highest-urgency element on the page.
            // #cc2f2f is 5.23:1. colors.ts created the -dark shades for exactly
            // this — "use `dark` whenever the colour carries words".
            <span className="inline-flex items-center rounded-full bg-danger-dark px-2 py-0.5 text-xs font-semibold text-background">
              {breachCount}
            </span>
          )}
        </div>
        {queueTotal > 0 && (
          <Link
            to="/admin/ops/action-queue"
            className="text-xs font-medium text-primary-ink hover:underline"
          >
            See all {queueTotal} →
          </Link>
        )}
      </div>

      <UniversalAsyncBoundary
        isLoading={items === null && !error}
        error={error}
        errorTitle="Could not load the action queue"
        isEmpty={all.length === 0}
        emptyTitle="Inbox zero"
        emptyDescription="Nothing actionable right now."
        emptyCompact
        loadingVariant="rows"
        loadingCount={3}
      >
        <ul className="space-y-2">
          {visible.map((row) => {
            const isBreach = row.priority === 'breach';
            return (
              <li key={row.key}>
                <Link
                  to={row.href}
                  className={`flex items-start justify-between gap-3 rounded-sm border px-3 py-2 text-sm transition-colors ${
                    isBreach
                      ? 'border-danger/30 bg-danger/5 hover:bg-danger/10'
                      : 'border-border bg-card-subtle hover:bg-card-hover'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className={`truncate font-medium ${isBreach ? 'text-danger' : 'text-foreground'}`}>
                      {row.title}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">{row.detail}</p>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">Open →</span>
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Expand/collapse. Rendered INSIDE the async boundary so it cannot
            appear over a loading skeleton or an empty state. Uses the shared
            Button (CLAUDE.md §4.2) — a raw <button> would carry neither the
            focus ring nor the 44px target. */}
        {(hiddenCount > 0 || expanded) && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full justify-center text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded ? (
              <>
                <ChevronUp aria-hidden />
                Show less
              </>
            ) : (
              <>
                <ChevronDown aria-hidden />
                Show {hiddenCount} more
              </>
            )}
          </Button>
        )}
      </UniversalAsyncBoundary>
    </Card>
  );
}

/** Shared shape of the two chart queries — only what the cards actually read. */
interface ChartQuery<T> {
  data: T[] | undefined;
  isLoading: boolean;
  error: unknown;
}

function queryError(e: unknown): string | null {
  return e instanceof Error ? e.message : e ? String(e) : null;
}

function CollectionsCard({ query }: { query: ChartQuery<CollectionsMonth> }) {
  const rows = query.data ?? [];
  // Show the last 6 of the 12 the view returns. Twelve points in a half-width
  // card puts the labels on top of each other, and the business is five months
  // old — the earlier months are all structural zeros.
  const recent = rows.slice(-6);
  const latest = recent[recent.length - 1];

  return (
    <Card interactive={false} className="p-5">
      <div className="mb-1 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="size-5 text-info" aria-hidden />
          <h2 className="font-display text-base font-bold tracking-tight text-foreground">
            Collected revenue
          </h2>
        </div>
        <Link to="/admin/reports" className="text-xs font-medium text-primary-ink hover:underline">
          Open reports →
        </Link>
      </div>
      {/* Named, because it is NOT the MRR on the tile above and the two will
          never agree. That one is what we bill; this is what arrived. */}
      <p className="mb-3 text-xs text-muted-foreground">
        Payments received per month — not the same measure as MRR.
      </p>

      <UniversalAsyncBoundary
        isLoading={query.isLoading}
        error={queryError(query.error)}
        errorTitle="Could not load collections"
        isEmpty={recent.length === 0}
        emptyTitle="No payments recorded"
        emptyCompact
        loadingVariant="rows"
        loadingCount={3}
      >
        {latest && (
          <div className="mb-2 flex items-baseline gap-3">
            <span className="font-display text-2xl font-bold tabular-nums text-foreground">
              {formatZARWhole(latest.collected_zar)}
            </span>
            {latest.is_partial && (
              <span className="text-xs font-semibold text-warning-dark">month in progress</span>
            )}
          </div>
        )}
        <ChartLine
          ariaLabel={`Collected revenue by month: ${recent
            .map((r) => `${monthLabel(r.month)} ${formatZARWhole(r.collected_zar)}${r.is_partial ? ' so far' : ''}`)
            .join(', ')}.`}
          // Point labels are EXACT; only the axis steps are compact. Sharing
          // one formatter labelled a R2 520 peak "3k" under a hero figure
          // reading R1 769 — the chart contradicting the number above it.
          format={formatZARWhole}
          axisFormat={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v)))}
          data={recent.map((r) => ({
            label: monthLabel(r.month),
            value: r.collected_zar,
            partial: r.is_partial,
          }))}
        />
      </UniversalAsyncBoundary>
    </Card>
  );
}

function RevenueMixCard({ query }: { query: ChartQuery<RevenueMixRow> }) {
  // Negative lines (discounts) cannot be a share of a whole — ChartStack drops
  // them, but filtering here keeps the total in the subtitle honest too.
  const rows = (query.data ?? []).filter((r) => r.revenue_zar > 0);
  const total = rows.reduce((s, r) => s + r.revenue_zar, 0);

  return (
    <Card interactive={false} className="p-5">
      <div className="mb-1 flex items-center gap-2">
        <Receipt className="size-5 text-info" aria-hidden />
        <h2 className="font-display text-base font-bold tracking-tight text-foreground">
          Where the money comes from
        </h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Paid invoice lines, all time{total > 0 ? ` · ${formatZARWhole(total)}` : ''}
      </p>

      <UniversalAsyncBoundary
        isLoading={query.isLoading}
        error={queryError(query.error)}
        errorTitle="Could not load revenue mix"
        isEmpty={rows.length === 0}
        emptyTitle="No paid invoice lines yet"
        emptyCompact
        loadingVariant="rows"
        loadingCount={2}
      >
        <ChartStack
          format={formatZARWhole}
          ariaLabel={`Revenue by product line: ${rows
            .map((r) => `${REVENUE_MIX_LABELS[r.target_kind] ?? r.target_kind} ${formatZARWhole(r.revenue_zar)}`)
            .join(', ')}.`}
          data={rows.map((r) => ({
            label: REVENUE_MIX_LABELS[r.target_kind] ?? r.target_kind,
            value: r.revenue_zar,
          }))}
        />
      </UniversalAsyncBoundary>
    </Card>
  );
}

function PaymentMixCard({ query }: { query: ChartQuery<PaymentMixRow> }) {
  const rows = query.data ?? [];
  const manual = rows.find((r) => r.gateway === 'eft_manual')?.payment_count ?? 0;
  const totalCount = rows.reduce((s, r) => s + r.payment_count, 0);

  return (
    <Card interactive={false} className="p-5">
      <div className="mb-1 flex items-center gap-2">
        <Receipt className="size-5 text-info" aria-hidden />
        <h2 className="font-display text-base font-bold tracking-tight text-foreground">
          How customers pay
        </h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        {totalCount > 0 ? `${totalCount} successful payments · all time` : 'All time'}
      </p>

      <UniversalAsyncBoundary
        isLoading={query.isLoading}
        error={queryError(query.error)}
        errorTitle="Could not load payment methods"
        isEmpty={rows.length === 0}
        emptyTitle="No payments recorded"
        emptyCompact
        loadingVariant="rows"
        loadingCount={3}
      >
        {/* scale="single": the gateway NAMES are the subject, so every bar takes
            the same hue. Colouring them by value would spend the identity
            channel re-encoding what bar length already shows. */}
        <ChartBars
          scale="single"
          format={(v) => String(v)}
          ariaLabel={`Payments by method: ${rows
            .map((r) => `${PAYMENT_GATEWAY_LABELS[r.gateway] ?? r.gateway} ${r.payment_count}`)
            .join(', ')}.`}
          data={rows.map((r) => ({
            label: PAYMENT_GATEWAY_LABELS[r.gateway] ?? r.gateway,
            value: r.payment_count,
            hint: formatZARWhole(r.collected_zar),
          }))}
        />
        {manual > 0 && (
          <p className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
            {manual} of {totalCount} arrive by EFT and are reconciled by hand.
          </p>
        )}
      </UniversalAsyncBoundary>
    </Card>
  );
}

/** '2026-08-01' → 'Aug'. Parsed as a plain date string, never through the
 *  browser's timezone — a UTC-midnight Date in SAST is still the same day, but
 *  relying on that is how month labels drift by one. */
function monthLabel(iso: string): string {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const m = Number(iso.slice(5, 7));
  return MONTHS[m - 1] ?? iso.slice(0, 7);
}

function ResellerCapacityRibbon({ snapshot }: { snapshot: DashboardSnapshot }) {
  const pct = snapshot.reseller_capacity_pct ?? 0;
  const max = snapshot.reseller_max_accounts ?? 0;
  const used = max > 0 ? Math.round((pct / 100) * max) : 0;
  // The local tone ladder is gone. It said amber from 60% while UsageBar — the
  // component that actually paints every other usage bar in the product — says
  // amber from 50%, so the same percentage rendered two different colours
  // depending on which screen you were on. ChartMeter derives both the bar and
  // the readout from UsageBar's single definition.
  // Net behavioural change: the 51–59% band now reads amber rather than green.
  // At the live 88% nothing changes; both ladders already said danger.

  return (
    <Card interactive={false} className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Server className="size-5 text-info" aria-hidden />
          <h2 className="font-display text-base font-bold tracking-tight text-foreground">
            Reseller capacity (rs61.cphost.co.za)
          </h2>
        </div>
        <Link
          to="/admin/hosting/server-health"
          className="text-xs font-medium text-primary-ink hover:underline"
        >
          Open server health →
        </Link>
      </div>
      <ChartMeter
        value={used}
        max={max}
        threshold={CAPACITY_ATTENTION_PCT}
        label={`${used} of ${max} accounts used`}
        ariaLabel={`Reseller capacity: ${used} of ${max} accounts used, ${pct} percent.`}
        warning={
          <>
            {Math.max(max - used, 0)} account{max - used === 1 ? '' : 's'} of headroom.
            Schedule the reseller upgrade decision (see Server health).
          </>
        }
      />
    </Card>
  );
}

/**
 * Cron health strip — TWO signals, and only one of them proves a job ran.
 *
 *   gateway   admin_cron_health() -> cron.job_run_details
 *             "the HTTP gateway replied". Records 'succeeded' for a 401 and
 *             for a 500 (CLAUDE.md §4.8).
 *   liveness  v_cron_health -> heartbeat + audit event
 *             "the function itself left a trace that it executed".
 *
 * F-01: this strip read ONLY the gateway. A job that had been dead for a
 * fortnight rendered a green pip, so the dashboard — the page staff actually
 * open — would have looked perfectly healthy through the 16-day June outage,
 * the 18-day July one and the 2-day August one. /admin/ops/cron-health has
 * carried both signals since F4b; the fix landed on the page nobody opens.
 *
 * THE LIVENESS SIGNAL OUTRANKS THE GATEWAY. `succeeded` + stale is not an
 * ambiguous state — it is the exact signature of a silent outage, so it paints
 * red. Anything else would let the weaker signal mask the stronger one, which
 * is the whole defect.
 *
 * "Not monitored" is a THIRD state, not a failure. In-database jobs (the
 * watchdog itself, the run-details prune) deliberately have no heartbeat, so a
 * missing liveness row means "nothing to check", never "dead" — and it must
 * not be rendered as reassuring green either.
 */
function CronHealthStrip({
  crons,
  liveness,
  error,
  livenessError,
}: {
  crons: CronHealthRow[] | null;
  liveness: CronLivenessRow[] | null;
  error: string | null;
  livenessError: string | null;
}) {
  const failing = (crons ?? []).filter((c) => c.last_run_status === 'failed').length;

  const livenessByName = new Map((liveness ?? []).map((l) => [l.job_name, l]));
  const silentJobs = (crons ?? []).filter(
    (c) => livenessByName.get(c.jobname)?.is_stale === true,
  );
  const silent = silentJobs.length;

  // A1 (2026-08-03): "last run" is derived here rather than read from the
  // snapshot. The snapshot used to carry last_cron_run_at, which forced the
  // dashboard view to call admin_cron_health_summary() — a full scan of
  // cron.job_run_details that made /admin time out. Same value, no coupling.
  const lastRun = (crons ?? [])
    .map((c) => c.last_run_start)
    .filter((t): t is string => !!t)
    .sort()
    .at(-1) ?? null;

  /** The one place the two signals are combined. ORDER IS THE POINT: `is_stale`
   *  is checked BEFORE the gateway status, so a job reporting 'succeeded' while
   *  its heartbeat has gone quiet reads as silent, not healthy. */
  function pipState(
    c: CronHealthRow,
  ): 'inactive' | 'silent' | 'failing' | 'ran' | 'unmonitored' | 'no-recent-runs' {
    if (!c.active) return 'inactive';
    const live = livenessByName.get(c.jobname);
    if (live?.is_stale === true) return 'silent';
    if (c.last_run_status === 'failed') return 'failing';
    // Heartbeat present and fresh — the only state that genuinely earns green.
    if (live && !live.is_stale) return 'ran';
    // No liveness row: an in-database job with no heartbeat by design. Amber,
    // not green — we do not know that it ran, and saying so in green is the
    // habit that made three outages invisible.
    if (!live && liveness !== null) return 'unmonitored';
    // NULL = no runs inside admin_cron_health()'s ~17-day window (A7), NOT
    // "never run". Grey is correct here; the cron-health page states it plainly.
    return c.last_run_status === 'succeeded' ? 'ran' : 'no-recent-runs';
  }

  const PIP: Record<ReturnType<typeof pipState>, { color: string; label: string }> = {
    silent: { color: 'bg-danger', label: 'SILENT — gateway says succeeded but the job left no trace' },
    failing: { color: 'bg-danger', label: 'failing' },
    ran: { color: 'bg-success', label: 'ran' },
    unmonitored: { color: 'bg-warning', label: 'not monitored — no heartbeat registered' },
    inactive: { color: 'bg-muted-foreground', label: 'inactive' },
    'no-recent-runs': { color: 'bg-muted-foreground', label: 'no recent runs' },
  };

  return (
    <Card interactive={false} className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="size-5 text-info" aria-hidden />
          <h2 className="font-display text-base font-bold tracking-tight text-foreground">Cron jobs</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {error ? <span className="text-danger">unavailable</span>
              : crons === null ? 'loading…' : (
              <>
                Last run {relativeTime(lastRun)}
                {/* SILENT LEADS. A silent job is a worse condition than a
                    failing one — a failure is visible and gets acted on, a
                    silence is what cost 36 days across three outages. */}
                {silent > 0 ? (
                  <span className="ml-1 font-semibold text-danger">· {silent} silent</span>
                ) : null}
                {failing > 0 ? <span className="ml-1 text-danger">· {failing} failing</span> : null}
                {/* Never let a liveness failure pass as "all clear": without it
                    every pip falls back to the gateway signal, which is the
                    state this panel was fixed to stop reporting as healthy. */}
                {livenessError ? (
                  <span className="ml-1 text-warning-dark">· liveness unavailable</span>
                ) : null}
              </>
            )}
          </span>
          {/* Verb + destination, the same shape as "See all N" and "Open server
              health". Three adjacent cards had three different link idioms. */}
          <Link to="/admin/ops/cron-health" className="text-xs font-medium text-primary-ink hover:underline">
            Open cron health →
          </Link>
        </div>
      </div>
      {error ? (
        // Honest UI: this panel used to swallow its error and sit on
        // "Loading cron health…" forever. admin_cron_health() is still slow
        // (16.6s, unindexable source table) and can exceed the 8s timeout.
        <p className="text-xs text-danger">Cron health unavailable — {error}</p>
      ) : crons === null ? (
        <p className="text-xs text-muted-foreground">Loading cron health…</p>
      ) : crons.length === 0 ? (
        <p className="text-xs text-muted-foreground">No scheduled jobs found.</p>
      ) : (
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {crons.map((c) => {
            const state = pipState(c);
            const pip = PIP[state];
            const live = livenessByName.get(c.jobname);
            return (
              <li
                key={c.jobid}
                className={`flex items-center gap-2 rounded-sm border px-3 py-2 text-xs ${
                  state === 'silent'
                    ? 'border-danger/30 bg-danger/5'
                    : 'border-border bg-card-subtle'
                }`}
                title={
                  state === 'silent' && live
                    ? `No heartbeat for ${live.hours_since ?? '?'}h (alarms after ${live.max_age_hours}h). The gateway reports "${c.last_run_status}" — that only means it replied.`
                    : c.last_run_error ?? undefined
                }
              >
                <span className={`inline-block size-2 shrink-0 rounded-full ${pip.color}`} aria-hidden />
                <span className="sr-only">{pip.label}:</span>
                <span
                  className={`truncate font-mono ${
                    state === 'silent' ? 'font-semibold text-danger' : 'text-foreground'
                  }`}
                >
                  {c.jobname}
                </span>
                <span className="ml-auto shrink-0 text-muted-foreground">{c.schedule}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function QuickActionCard({
  to,
  icon: Icon,
  title,
  description,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <Link to={to} className="block">
      <Card tone="hairline" interactive className="flex h-full flex-col gap-2 p-5">
        <span className="inline-flex size-10 items-center justify-center rounded-sm bg-trust/10 text-trust">
          <Icon className="size-5" aria-hidden />
        </span>
        <div className="flex-1">
          <h3 className="font-display text-sm font-bold tracking-tight text-foreground">{title}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{description}</p>
        </div>
        {/* NOT a <Button>. The whole Card is already inside a <Link>, and a real
            button here nests one interactive element inside another: invalid
            HTML, two tab stops for one destination, and unpredictable
            screen-reader announcement. This is the affordance, not a control —
            the Link takes its accessible name from the heading above. */}
        <span
          aria-hidden
          className="self-start rounded-sm bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground"
        >
          Open
        </span>
      </Card>
    </Link>
  );
}
